/* The sign-in screen and the enterprise enquiry form.

   The prototype signed people in the moment they typed an email. Sign-in is real now, so
   the same one-line form gains a second step: request a code, then enter it. The markup
   and classes are unchanged, so the card looks identical. */

import { $ } from "../util.js";
import { api } from "../api.js";
import { toast } from "../toast.js";
import { state } from "../state.js";

const EMAIL_RE = /.+@.+\..+/;
let pendingEmail = null;

export function initAuthScreen({ onSignedIn }) {
  wireGoogle();
  wireEmail(onSignedIn);
}

/* ---------- Google ---------- */

function wireGoogle() {
  $("btn-google").addEventListener("click", async () => {
    if (!state.googleClientId) {
      toast("Google sign-in isn't configured yet — continue with email");
      $("auth-email").focus();
      return;
    }
    try {
      await loadGoogleIdentity();
      /* Google's one-tap prompt is often suppressed — no Google session, a previous
         dismissal, or the browser blocking third-party state. When that happens, show
         Google's own button instead, which always works. */
      window.google.accounts.id.prompt(notification => {
        const skipped = notification.isNotDisplayed?.() || notification.isSkippedMoment?.();
        if (skipped) showGoogleButton();
      });
    } catch {
      toast("Google sign-in could not start");
    }
  });
}

/* Replaces our branded button with the official Google one. */
function showGoogleButton() {
  if (document.getElementById("gsi-button")) return;
  const ours = $("btn-google");
  const holder = document.createElement("div");
  holder.id = "gsi-button";
  holder.style.display = "flex";
  holder.style.justifyContent = "center";
  ours.parentNode.insertBefore(holder, ours);
  ours.classList.add("hidden");
  window.google.accounts.id.renderButton(holder, {
    theme: "outline", size: "large", width: 320, text: "continue_with", shape: "pill",
  });
}

/* Loads Google Identity Services on demand and hands the credential to the server.
   Nothing is requested until the owner supplies a client ID. */
function loadGoogleIdentity() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => {
      window.google.accounts.id.initialize({
        client_id: state.googleClientId,
        callback: async ({ credential }) => {
          try {
            const { user, projectsKept } = await api.google(credential);
            window.dispatchEvent(new CustomEvent("oqc:signed-in", { detail: { user, projectsKept } }));
          } catch (err) {
            toast(err.message);
          }
        },
      });
      resolve();
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/* ---------- email ---------- */

function wireEmail(onSignedIn) {
  const submit = () => (pendingEmail ? verify(onSignedIn) : requestCode());
  $("btn-email").addEventListener("click", submit);
  $("auth-email").addEventListener("keydown", e => { if (e.key === "Enter") submit(); });
  window.addEventListener("oqc:signed-in", e => onSignedIn(e.detail.user, { projectsKept: e.detail.projectsKept }));
}

async function requestCode() {
  const email = $("auth-email").value.trim();
  if (!EMAIL_RE.test(email)) { toast("Enter a valid email address"); $("auth-email").focus(); return; }
  setBusy(true);
  try {
    const res = await api.requestCode(email);
    pendingEmail = email;
    showCodeStep(res.devCode);
  } catch (err) {
    toast(err.message);
  } finally {
    setBusy(false);
  }
}

async function verify(onSignedIn) {
  const code = $("auth-email").value.trim();
  if (!/^\d{6}$/.test(code)) { toast("Enter the 6-digit code from your email"); return; }
  setBusy(true);
  try {
    const { user, projectsKept } = await api.verifyCode(pendingEmail, code);
    pendingEmail = null;
    onSignedIn(user, { projectsKept });
  } catch (err) {
    toast(err.message);
  } finally {
    setBusy(false);
  }
}

/* Reuses the same input for the code, so the card does not change shape. */
function showCodeStep(devCode) {
  const input = $("auth-email");
  input.value = "";
  input.type = "text";
  input.inputMode = "numeric";
  input.maxLength = 6;
  input.placeholder = "6-digit code";
  input.focus();
  $("btn-email").textContent = "Verify";
  const fine = document.querySelector("#screen-auth .afine");
  fine.innerHTML = `We sent a sign-in code to <b>${pendingEmail}</b>. It expires in five minutes. ` +
    `<a href="#" id="auth-restart" style="color:var(--violet);font-weight:700;text-decoration:none">Use a different email</a>`;
  $("auth-restart").addEventListener("click", e => { e.preventDefault(); resetEmailStep(); });
  toast(devCode ? `Sign-in code: ${devCode}` : "Check your email for the sign-in code");
}

export function resetEmailStep() {
  pendingEmail = null;
  const input = $("auth-email");
  input.value = "";
  input.type = "email";
  input.removeAttribute("maxlength");
  input.placeholder = "you@company.com";
  $("btn-email").textContent = "Continue";
  const fine = document.querySelector("#screen-auth .afine");
  if (fine) fine.textContent = "Free to use. By continuing you agree to receive occasional product updates from Botpresso.";
}

function setBusy(busy) {
  $("btn-email").disabled = busy;
}
